// A filter on one column of a list: the funnel in a column header. One value per
// column, written the way the API reads it (services/api lib/columnFilters.ts),
// so the list sends it as it is:
//
//   text     "bosch"                   contains, case aside
//   options  "DRAFT,SENT"              one of these
//   date     "2026-10-01..2026-10-31"  from and to, both days included; either side may be empty
//   number   "500..1000"               from and to, both included; either side may be empty

export interface FilterOption {
  value: string;
  labelKey: string;
}

export type ColumnFilterDef =
  | { kind: "text" }
  | { kind: "options"; options: readonly FilterOption[] }
  | { kind: "date" }
  /** `unitKey`: what the numbers count, when the cells say it otherwise — euros for "€ 1.025,66". */
  | { kind: "number"; unitKey?: string };

/** Per column key, the value as written above; a column without a filter is not in it. */
export type FilterValues = Readonly<Record<string, string>>;

const PREFIX = "filter.";

/** The column filters a list's search can carry: filter.<key>, beside its own keys. */
export type FilterSearch = { [key: `filter.${string}`]: string | undefined };

/** The column filters in a list's address bar. */
export function filtersOf(search: object): FilterValues {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(search)) {
    if (key.startsWith(PREFIX) && typeof value === "string" && value.trim() !== "") out[key.slice(PREFIX.length)] = value;
  }
  return out;
}

/** The same, as query parameters for the API. */
export function filterParams(values: FilterValues): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) out[`${PREFIX}${key}`] = value;
  return out;
}

/** The column filters of a route's search, for its validateSearch: kept as they are. */
export function filterSearch(search: object): FilterSearch {
  return filterParams(filtersOf(search)) as FilterSearch;
}

/** The address-bar key of one column's filter. */
export function filterParam(key: string): string {
  return `${PREFIX}${key}`;
}

/** "a..b" as its two sides; a single value is both. */
export function rangeOf(value: string | undefined): [string, string] {
  if (!value) return ["", ""];
  if (!value.includes("..")) return [value, value];
  const [from = "", to = ""] = value.split("..");
  return [from.trim(), to.trim()];
}

/** Two sides as one value; both empty is no filter. */
export function rangeValue(from: string, to: string): string {
  const a = from.trim();
  const b = to.trim();
  if (!a && !b) return "";
  if (a && a === b) return a;
  return `${a}..${b}`;
}

/** The chosen values of an options filter. */
export function optionsOf(value: string | undefined): string[] {
  return value
    ? value
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean)
    : [];
}

// ─── What most columns filter with ──────────────────────────────────────────

export const TEXT_FILTER: ColumnFilterDef = { kind: "text" };
export const DATE_FILTER: ColumnFilterDef = { kind: "date" };
export const NUMBER_FILTER: ColumnFilterDef = { kind: "number" };

/** A choice from a map of values to label keys, in the map's order. */
export function optionsFilter(labelKeys: Readonly<Record<string, string>>): ColumnFilterDef {
  return { kind: "options", options: Object.entries(labelKeys).map(([value, labelKey]) => ({ value, labelKey })) };
}
